import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import "./OnboardingReport.css";

// Shared by the admin "Onboarding Managers" pane and the manager's own dashboard.

export const referralLink = (code) => `${window.location.origin}/signup?role=artist&referral=${encodeURIComponent(code || "")}`;

export function CopyButton({ text, label = "Copy", testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked: the text stays visible to copy by hand */ }
  };
  return (
    <button type="button" className="btn btn-ghost btn-xs" onClick={copy} data-testid={testId}>
      {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />} {copied ? "Copied" : label}
    </button>
  );
}

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const LINKED_VIA = { link: "Referral link", code: "Typed code", admin: "Assigned by admin" };

export function OnboardingSummary({ summary }) {
  if (!summary) return null;
  const tiles = [
    ["Artists onboarded", summary.artists],
    ["Onboarding finished", summary.onboarding_completed],
    ["KYC submitted", summary.kyc_submitted],
    ["KYC approved", summary.kyc_approved],
    ["Live on BookTalent", summary.live],
    ["Questionnaire done", summary.questionnaire_complete],
    ["Avg. questionnaire", `${summary.avg_questionnaire_percent}%`],
    ["Avg. profile", `${summary.avg_profile_percent}%`],
  ];
  return (
    <div className="ob-summary" data-testid="onboarding-summary">
      {tiles.map(([label, value]) => (
        <div key={label} className="ob-summary-tile">
          <div className="ob-summary-value">{value}</div>
          <div className="ob-summary-label">{label}</div>
        </div>
      ))}
    </div>
  );
}

const STEP_CHIPS = [
  ["basic_profile", "Profile"],
  ["branding", "Bio"],
  ["media", "Photos"],
  ["packages", "Packages"],
  ["availability", "Calendar"],
];

const kycTone = (row) => (row.is_live ? "green" : row.kyc_approved ? "green" : row.kyc_status === "kyc_rejected" || row.kyc_status === "kyc_changes_required" ? "red" : row.kyc_submitted ? "amber" : "muted");

export function OnboardingArtistTable({ artists, showContact = true, emptyText = "No artists linked yet." }) {
  if (!artists?.length) return <div className="empty"><p className="text-muted">{emptyText}</p></div>;
  return (
    <div className="table-responsive ob-table-wrap">
      <table className="table ob-table" data-testid="onboarding-artist-table">
        <thead>
          <tr>
            <th>Artist</th>
            <th>Joined</th>
            <th>Profile setup</th>
            <th>KYC</th>
            <th>Questionnaire</th>
            <th>Profile</th>
            <th>Next step</th>
          </tr>
        </thead>
        <tbody>
          {artists.map((row) => (
            <tr key={row.artist_id} data-testid={`onboarding-row-${row.artist_id}`}>
              <td>
                <Link to={`/staff/artists/${row.artist_id}`} className="fw-600" data-testid={`onboarding-review-${row.artist_id}`}>{row.stage_name}</Link>
                <div className="text-muted fs-11">{[...(row.categories || []).slice(0, 2), row.city].filter(Boolean).join(" · ")}</div>
                {showContact && <div className="text-muted fs-11">{[row.email, row.phone].filter(Boolean).join(" · ")}</div>}
              </td>
              <td>
                <div className="fs-12">{formatDate(row.signed_up_at)}</div>
                <div className="text-muted fs-11">{LINKED_VIA[row.linked_via] || "—"}</div>
              </td>
              <td>
                <div className="ob-steps">
                  {STEP_CHIPS.map(([id, label]) => (
                    <span key={id} className={`ob-step ${row.steps?.[id] ? "done" : ""}`} title={`${label}: ${row.steps?.[id] ? "done" : "missing"}`}>
                      {row.steps?.[id] ? "✓" : "○"} {label}
                    </span>
                  ))}
                </div>
              </td>
              <td>
                <span className={`ob-pill ob-pill-${kycTone(row)}`}>{row.kyc_label}</span>
                {row.is_live && row.went_live_at && <div className="text-muted fs-11 mt-4">Live since {formatDate(row.went_live_at)}</div>}
              </td>
              <td>
                <div className="fs-12">{row.questionnaire_answered}/{row.questionnaire_total}</div>
                <div className="ob-bar" aria-label={`${row.questionnaire_percent}% answered`}><span style={{ width: `${row.questionnaire_percent}%` }} /></div>
              </td>
              <td><span className="fw-600">{row.profile_percent}%</span></td>
              <td className="fs-12">
                {row.next_step || (row.is_live ? "All set" : "—")}
                {row.missing?.length > 0 && (
                  <div className="text-muted fs-11 mt-4" title={row.missing.join(", ")}>Missing: {row.missing.slice(0, 2).join(", ")}{row.missing.length > 2 ? ` +${row.missing.length - 2}` : ""}</div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
